/**
 * Currency configuration for formatting prices
 */
export interface CurrencyFormatConfig {
  symbol: string;
  decimals: number;
  symbolPosition: 'before' | 'after';
}

const CURRENCY_FORMATS: Record<string, CurrencyFormatConfig> = {
  usd: { symbol: '$', decimals: 2, symbolPosition: 'before' },
  eur: { symbol: '€', decimals: 2, symbolPosition: 'after' },
  gbp: { symbol: '£', decimals: 2, symbolPosition: 'before' },
  cad: { symbol: 'CA$', decimals: 2, symbolPosition: 'before' },
  mxn: { symbol: 'MX$', decimals: 2, symbolPosition: 'before' },
  jpy: { symbol: '¥', decimals: 0, symbolPosition: 'before' },
  aud: { symbol: 'A$', decimals: 2, symbolPosition: 'before' },
  brl: { symbol: 'R$', decimals: 2, symbolPosition: 'before' },
  thb: { symbol: '฿', decimals: 2, symbolPosition: 'before' },
  zar: { symbol: 'R', decimals: 2, symbolPosition: 'before' },
};

const DEFAULT_FORMAT: CurrencyFormatConfig = {
  symbol: '$',
  decimals: 2,
  symbolPosition: 'before',
};

/**
 * Format a price in the smallest currency unit (cents, pence, etc.) to a display string
 * @param amount - Amount in smallest currency unit (e.g., cents)
 * @param currency - Currency code (e.g., 'usd', 'eur', 'gbp')
 * @param options - Additional formatting options
 */
export function formatPrice(
  amount: number,
  currency: string = 'usd',
  options?: {
    showCurrency?: boolean; // Include currency code after the price
    compact?: boolean; // Use compact notation for large numbers
  }
): string {
  const format = CURRENCY_FORMATS[currency.toLowerCase()] ?? DEFAULT_FORMAT;
  const divisor = Math.pow(10, format.decimals);
  const value = amount / divisor;

  const formattedNumber = value.toLocaleString(undefined, {
    minimumFractionDigits: format.decimals,
    maximumFractionDigits: format.decimals,
  });

  let result: string;
  if (format.symbolPosition === 'before') {
    result = `${format.symbol}${formattedNumber}`;
  } else {
    result = `${formattedNumber}${format.symbol}`;
  }

  if (options?.showCurrency) {
    result += ` ${currency.toUpperCase()}`;
  }

  return result;
}

/**
 * Format a price per month (for subscription display)
 * @param totalAmount - Total amount in smallest currency unit
 * @param months - Number of months the price covers
 * @param currency - Currency code
 */
export function formatPricePerMonth(
  totalAmount: number,
  months: number = 1,
  currency: string = 'usd'
): string {
  const monthlyAmount = Math.round(totalAmount / months);
  return `${formatPrice(monthlyAmount, currency)}/m`;
}

